// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Settings.js
// User settings, as tabs (sidebar on desktop, tab bar on phones; ?tab= in the URL): appearance (theme + candle style),
// chart indicator favourites (Chart 2), lists, annotations (mine / shared with me, searchable + paged), account.
// Discord alerts (Connect Discord) is parked.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';
import RankingEditor from '../components/RankingEditor';
import { INDICATORS } from '../chart/indicators';
import { getAppearance, saveAppearance } from '../appearance';

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page:      { minHeight: '100vh', background: 'var(--bg)' },
  nav:       { background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 32px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo:      { fontFamily: "'JetBrains Mono',monospace", fontSize: '13px', fontWeight: 500, letterSpacing: '0.1em', color: 'var(--accent)', textTransform: 'uppercase' },
  navRight:  { display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: 'var(--text-muted)' },
  navLink:   { color: 'var(--accent)', fontSize: '13px', textDecoration: 'none' },
  logoutBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: '13px', fontFamily: "'Space Grotesk',sans-serif", padding: '6px 12px', borderRadius: 'var(--radius)' },

  content:   { maxWidth: '1100px', margin: '0 auto', padding: '48px 32px' },
  pageTitle: { fontSize: '24px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '4px' },
  pageSub:   { fontSize: '14px', color: 'var(--text-muted)', marginBottom: '48px' },

  section:      { marginBottom: '48px' },
  sectionHead:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', borderBottom: '2px solid var(--border)', paddingBottom: '10px' },
  sectionTitle: { fontSize: '16px', fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' },

  card:    { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '20px', marginBottom: '12px' },
  cardRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' },
  cardInfo:{ flex: 1 },
  cardName:{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' },
  cardMeta:{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace" },

  btn:       { background: 'var(--accent)', color: 'var(--on-accent)', border: 'none', borderRadius: 'var(--radius)', padding: '7px 16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnSm:     { background: 'var(--accent)', color: 'var(--on-accent)', border: 'none', borderRadius: 'var(--radius)', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnGhost:  { background: 'none', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' },
  btnDanger: { background: 'none', color: 'var(--error)', border: '1px solid var(--error)', borderRadius: 'var(--radius)', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' },

  input:    { width: '100%', padding: '7px 10px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Space Grotesk',sans-serif", boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '7px 10px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Space Grotesk',sans-serif", boxSizing: 'border-box', resize: 'vertical', minHeight: '60px' },
  label:    { fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'JetBrains Mono',monospace", display: 'block', marginBottom: '4px' },
  formRow:  { marginBottom: '12px' },
  select:   { padding: '5px 8px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'JetBrains Mono',monospace" },

  searchWrap: { position: 'relative' },
  dropdown:   { position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 200, maxHeight: '220px', overflowY: 'auto' },
  dropItem:   { padding: '7px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px solid var(--border)', fontFamily: "'JetBrains Mono',monospace" },

  tableWrap: { border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 1.5)', overflowX: 'auto', background: 'var(--surface)' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: "'JetBrains Mono',monospace" },
  th:        { padding: '9px 12px', textAlign: 'left', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-muted)', fontSize: '10px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' },
  td:        { padding: '8px 12px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' },

  tickerLink: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", fontSize: '12px', textDecoration: 'underline' },

  badge: (type) => ({
    fontSize: '10px', padding: '2px 6px', borderRadius: '3px', fontWeight: 700,
    background: type === 'etf' ? 'var(--info-dim)' : type === 'Public' ? 'var(--success-dim)' : type === 'Share' ? 'var(--warning-dim)' : 'var(--surface-2)',
    color: type === 'etf' ? 'var(--info)' : type === 'Public' ? 'var(--success)' : type === 'Share' ? 'var(--warning)' : 'var(--text-muted)',
  }),

  empty:   { padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' },
  error:   { color: 'var(--error)', fontSize: '12px', marginTop: '4px' },
  success: { color: 'var(--success)', fontSize: '12px', marginTop: '4px' },

  expandBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: '11px', fontFamily: "'JetBrains Mono',monospace", padding: '0 4px' },
  divider:   { border: 'none', borderTop: '1px solid var(--border)', margin: '16px 0' },
  loadingScreen: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-muted)', fontSize: '13px' },

  // Settings layout: sidebar (wide) / tab bar (phone), panel heading, toolbars
  sideNav:  { width: '210px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '2px' },
  sideItem: on => ({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '14px', fontFamily: "'Space Grotesk',sans-serif", fontWeight: on ? 600 : 400, background: on ? 'var(--accent-dim)' : 'none', color: on ? 'var(--text)' : 'var(--text-muted)', boxShadow: on ? 'inset 3px 0 0 var(--accent)' : 'none' }),
  tabBar:   { display: 'flex', gap: '6px', overflowX: 'auto', width: '100%', paddingBottom: '4px', WebkitOverflowScrolling: 'touch' },
  tabItem:  on => ({ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, minHeight: '40px', padding: '0 14px', borderRadius: '999px', cursor: 'pointer', fontSize: '13px', fontFamily: "'Space Grotesk',sans-serif", fontWeight: on ? 600 : 400, border: on ? '1px solid var(--accent)' : '1px solid var(--border)', background: on ? 'var(--accent)' : 'var(--surface)', color: on ? 'var(--on-accent)' : 'var(--text-muted)' }),
  tabCount: on => ({ fontSize: '11px', fontFamily: "'JetBrains Mono',monospace", padding: '1px 7px', borderRadius: '999px', background: on ? 'var(--surface)' : 'var(--surface-2)', color: on ? 'var(--text)' : 'var(--text-muted)' }),
  panelHead:  { marginBottom: '18px' },
  panelTitle: { margin: 0, fontSize: '18px', fontWeight: 700, letterSpacing: '-0.01em' },
  panelDesc:  { margin: '2px 0 0', fontSize: '13px', color: 'var(--text-muted)' },
  toolbar:    { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '12px' },
  count:      { fontSize: '12px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' },
  kv:         { display: 'flex', gap: '16px', padding: '6px 0', fontSize: '14px', borderBottom: '1px solid var(--border)' },
  kvKey:      { width: '110px', color: 'var(--text-muted)' },

  // Share row inside annotation expand
  shareRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' },
  shareUser: { fontSize: '12px', fontFamily: "'JetBrains Mono',monospace", flex: 1, color: 'var(--text)' },
};

// ── Open ticker in new tab ────────────────────────────────────────────────────
function openChart(ticker) {
  window.open(`/chart?t=${encodeURIComponent(ticker)}`, '_blank');
}

// ── Ticker search input with autocomplete ─────────────────────────────────────
function TickerSearch({ onSelect, placeholder = 'Search ticker…' }) {
  const [q,       setQ]       = useState('');
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const timer   = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    if (!val.trim()) { setResults([]); setOpen(false); return; }
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get('/lists/instruments/search', { params: { q: val } })
        .then(r => { setResults(r.data.results || []); setOpen(true); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
  };

  const pick = (item) => { onSelect(item); setQ(''); setResults([]); setOpen(false); };

  return (
    <div ref={wrapRef} style={S.searchWrap}>
      <input style={S.input} value={q} onChange={e => search(e.target.value)} placeholder={placeholder} autoComplete="off" />
      {loading && <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'var(--text-muted)' }}>…</span>}
      {open && results.length > 0 && (
        <div style={S.dropdown}>
          {results.map(r => (
            <div key={r.instrument_id} style={S.dropItem}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
              onMouseDown={() => pick(r)}
            >
              <span style={{ fontWeight: 600 }}>{r.symbol}:{r.exchange}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>{r.name}</span>
              <span style={S.badge(r.asset_type)}>{r.asset_type}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Username search input with autocomplete ───────────────────────────────────
function UserSearch({ onSelect, placeholder = 'Search username…' }) {
  const [q,       setQ]       = useState('');
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const timer   = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    if (val.trim().length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get('/users/search', { params: { q: val } })
        .then(r => { setResults(r.data.results || []); setOpen(true); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
  };

  const pick = (item) => { onSelect(item); setQ(''); setResults([]); setOpen(false); };

  return (
    <div ref={wrapRef} style={S.searchWrap}>
      <input style={{ ...S.input, width: '200px' }} value={q} onChange={e => search(e.target.value)} placeholder={placeholder} autoComplete="off" />
      {loading && <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'var(--text-muted)' }}>…</span>}
      {open && results.length > 0 && (
        <div style={{ ...S.dropdown, width: '200px' }}>
          {results.map(r => (
            <div key={r.id} style={S.dropItem}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
              onMouseDown={() => pick(r)}
            >
              {r.username}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Annotation share manager — shown inline when privacy = Share ──────────────
function ShareManager({ ann }) {
  const [shares,  setShares]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState('');

  useEffect(() => {
    setLoading(true);
    api.get('/annotations/shares', { params: { id: ann.id } })
      .then(r => setShares(r.data.shares || []))
      .catch(() => setShares([]))
      .finally(() => setLoading(false));
  }, [ann.id]);

  const addUser = async (user) => {
    try {
      await api.post('/annotations/shares/add', { annotation_id: ann.id, username: user.username });
      setShares(prev => [...(prev || []), { id: user.id, username: user.username, shared_at: new Date().toISOString() }]);
      setErr('');
    } catch (e) { setErr(e.response?.data?.error || 'Failed to add user'); }
  };

  const removeUser = async (userId) => {
    try {
      await api.post('/annotations/shares/remove', { annotation_id: ann.id, user_id: userId });
      setShares(prev => prev.filter(s => s.id !== userId));
    } catch { /* ignore */ }
  };

  if (loading) return <div style={{ fontSize: '11px', color: 'var(--text-muted)', padding: '4px 0' }}>Loading shares…</div>;

  return (
    <div style={{ marginTop: '8px', padding: '10px', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'JetBrains Mono',monospace", marginBottom: '8px' }}>Shared With</div>
      {shares && shares.length > 0 ? shares.map(s => (
        <div key={s.id} style={S.shareRow}>
          <span style={S.shareUser}>{s.username}</span>
          <button style={S.btnDanger} onClick={() => removeUser(s.id)}>Remove</button>
        </div>
      )) : (
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>No users added yet.</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
        <UserSearch onSelect={addUser} placeholder="Add username…" />
      </div>
      {err && <div style={S.error}>{err}</div>}
    </div>
  );
}

// ── Create List form ──────────────────────────────────────────────────────────
function CreateListForm({ onCreated }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [err,  setErr]  = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api.post('/lists', { name: name.trim(), description: desc.trim() || null });
      onCreated(r.data.list);
      setName(''); setDesc('');
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to create list');
    } finally { setBusy(false); }
  };

  return (
    <div style={S.card}>
      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>New List</div>
      <div style={S.formRow}>
        <label style={S.label}>Name</label>
        <input style={S.input} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. ASX Watchlist" maxLength={100} />
      </div>
      <div style={S.formRow}>
        <label style={S.label}>Description (optional)</label>
        <textarea style={S.textarea} value={desc} onChange={e => setDesc(e.target.value)} placeholder="What's this list for?" />
      </div>
      {err && <div style={S.error}>{err}</div>}
      <button style={S.btn} onClick={submit} disabled={busy}>{busy ? 'Creating…' : 'Create List'}</button>
    </div>
  );
}

// ── Single list card ──────────────────────────────────────────────────────────
function ListCard({ list, onDeleted, onUpdated }) {
  const [expanded,     setExpanded]     = useState(false);
  const [showRanking,  setShowRanking]  = useState(false);
  const [items,        setItems]        = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [editing,      setEditing]      = useState(false);
  const [editName,     setEditName]     = useState(list.name);
  const [editDesc,     setEditDesc]     = useState(list.description || '');
  const [editErr,      setEditErr]      = useState('');
  const [deleting,     setDeleting]     = useState(false);
  const [msg,          setMsg]          = useState('');

  const loadItems = useCallback(() => {
    if (items !== null) return;
    setLoadingItems(true);
    api.get(`/lists/${list.id}/items`)
      .then(r => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoadingItems(false));
  }, [list.id, items]);

  const toggle = () => { if (!expanded) loadItems(); setExpanded(v => !v); };

  const saveEdit = async () => {
    if (!editName.trim()) { setEditErr('Name required'); return; }
    try {
      const r = await api.patch(`/lists/${list.id}`, { name: editName.trim(), description: editDesc.trim() || null });
      onUpdated(r.data.list);
      setEditing(false); setEditErr('');
    } catch (e) { setEditErr(e.response?.data?.error || 'Failed to save'); }
  };

  const deleteList = async () => {
    if (!window.confirm(`Delete list "${list.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try { await api.delete(`/lists/${list.id}`); onDeleted(list.id); }
    catch { setDeleting(false); }
  };

  const addTicker = async (instrument) => {
    try {
      const r = await api.post(`/lists/${list.id}/items`, { ticker: instrument.ticker });
      setItems(prev => [...(prev || []), r.data.item]);
      onUpdated({ ...list, item_count: (list.item_count || 0) + 1 });
      setMsg('Added'); setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      // 409 = already on list, treat as success
      if (e.response?.status === 409) { setMsg('Already on list'); setTimeout(() => setMsg(''), 2000); }
      else { setMsg(e.response?.data?.error || 'Error'); setTimeout(() => setMsg(''), 2000); }
    }
  };

  const removeTicker = async (item) => {
    try {
      await api.delete(`/lists/${list.id}/items/${item.instrument_id}`);
      setItems(prev => prev.filter(i => i.instrument_id !== item.instrument_id));
      onUpdated({ ...list, item_count: Math.max(0, (list.item_count || 0) - 1) });
    } catch { /* ignore */ }
  };

  return (
    <div style={S.card}>
      {editing ? (
        <>
          <div style={S.formRow}>
            <label style={S.label}>Name</label>
            <input style={S.input} value={editName} onChange={e => setEditName(e.target.value)} maxLength={100} />
          </div>
          <div style={S.formRow}>
            <label style={S.label}>Description</label>
            <textarea style={S.textarea} value={editDesc} onChange={e => setEditDesc(e.target.value)} />
          </div>
          {editErr && <div style={S.error}>{editErr}</div>}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={S.btn} onClick={saveEdit}>Save</button>
            <button style={S.btnGhost} onClick={() => { setEditing(false); setEditName(list.name); setEditDesc(list.description || ''); }}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div style={S.cardRow}>
            <div style={S.cardInfo}>
              <div style={S.cardName}>{list.name}</div>
              {list.description && <div style={S.cardMeta}>{list.description}</div>}
              <div style={{ ...S.cardMeta, marginTop: '4px' }}>{list.item_count} ticker{list.item_count !== 1 ? 's' : ''} · {list.privacy_level}</div>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
              <button style={S.expandBtn} onClick={toggle}>{expanded ? '▲ Hide' : '▼ Tickers'}</button>
              <button style={S.btnGhost} onClick={() => setShowRanking(v => !v)}>{showRanking ? '✕ Ranking' : '⚙ Ranking'}</button>
              <button style={S.btnGhost} onClick={() => setEditing(true)}>Edit</button>
              <button style={S.btnDanger} onClick={deleteList} disabled={deleting}>Delete</button>
            </div>
          </div>

          {showRanking && <RankingEditor listId={list.id} />}

          {expanded && (
            <>
              <hr style={S.divider} />
              <div style={{ marginBottom: '10px' }}>
                <label style={S.label}>Add Ticker</label>
                <TickerSearch onSelect={addTicker} placeholder="Search to add…" />
                {msg && <div style={S.success}>{msg}</div>}
              </div>
              {loadingItems ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Loading…</div>
              ) : items && items.length > 0 ? (
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Ticker</th>
                        <th style={S.th}>Name</th>
                        <th style={S.th}>Type</th>
                        <th style={S.th}>Exch</th>
                        <th style={S.th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.instrument_id}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}
                        >
                          <td style={S.td}>
                            <button style={S.tickerLink} onClick={() => openChart(item.ticker)}>{item.symbol}</button>
                          </td>
                          <td style={{ ...S.td, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name || '—'}</td>
                          <td style={S.td}><span style={S.badge(item.asset_type)}>{item.asset_type}</span></td>
                          <td style={S.td}>{item.exchange}</td>
                          <td style={S.td}>
                            <button style={S.btnDanger} onClick={() => removeTicker(item)}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={S.empty}>No tickers on this list yet.</div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Discord section ───────────────────────────────────────────────────────────
// Connect Discord so the bot sends this user's alerts to them privately (backend/discord_link.py).
// /settings?discord=ok|error&why=... is where the Discord sign-in comes back to.
// eslint-disable-next-line no-unused-vars
function DiscordSection() {
  const [st, setSt] = useState(null);
  const [msg, setMsg] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return q.get('discord') === 'ok' ? 'Connected - check Discord for a message from the bot.'
         : q.get('discord') === 'error' ? `Couldn't connect: ${q.get('why') || 'unknown error'}` : '';
  });
  const load = () => api.get('/discord/status').then(r => setSt(r.data)).catch(() => setSt({ linked: false }));
  useEffect(() => { load(); }, []);
  if (!st) return <div style={S.empty}>Loading…</div>;
  const test = async () => {
    setMsg('Sending…');
    try { const r = await api.post('/discord/test'); setMsg(r.data.ok ? 'Test message sent.' : `Not delivered: ${r.data.error}`); }
    catch (_) { setMsg('Not delivered.'); }
    load();
  };
  const toggle = async on => { await api.post('/discord/settings', { alerts_enabled: on }).catch(() => {}); load(); };
  const unlink = async () => { if (!window.confirm('Disconnect Discord? Alerts will still appear under the bell.')) return; await api.delete('/discord/link').catch(() => {}); setMsg(''); load(); };
  return (
    <div style={S.card}>
      <div style={{ ...S.cardMeta, marginBottom: '12px' }}>
        Get your chart alerts as private messages from the woody.wtf bot. Connecting adds you to the woody.wtf Discord server
        (the bot can only message people it shares a server with). Alerts always appear under the 🔔 bell as well.
      </div>
      {!st.linked ? (
        st.configured === false
          ? <div style={S.cardMeta}>Discord sign-in isn't set up on the server yet.</div>
          : <button style={S.btn} onClick={() => { window.location.href = '/api/discord/connect'; }}>Connect Discord</button>
      ) : (
        <>
          <div style={S.cardName}>Connected as {st.name}</div>
          <div style={{ ...S.cardMeta, margin: '4px 0 10px', color: st.dm_ok === false ? 'var(--error)' : 'var(--text-muted)' }}>
            {st.dm_ok === false ? `Can't deliver: ${st.dm_error || 'Discord refused the message'}` : st.dm_ok ? 'Messages are getting through.' : 'No message sent yet.'}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '12px', cursor: 'pointer' }}>
            <input type="checkbox" checked={st.alerts_enabled} onChange={e => toggle(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
            Send my alerts to Discord
          </label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button style={S.btnSm} onClick={test}>Send test message</button>
            <button style={S.btnGhost} onClick={() => { window.location.href = '/api/discord/connect'; }}>Reconnect</button>
            <button style={S.btnDanger} onClick={unlink}>Disconnect</button>
          </div>
        </>
      )}
      {msg && <div style={{ ...S.cardMeta, marginTop: '10px', color: msg.startsWith('Couldn') || msg.startsWith('Not') ? 'var(--error)' : 'var(--accent)' }}>{msg}</div>}
    </div>
  );
}

// ── Chart indicators section ──────────────────────────────────────────────────
// Ticked indicators show first in Chart 2's indicator picker. Saved in the user's chart
// prefs (/api/users/chart-prefs), keeping the default layout saved from the chart.
// ── Appearance section ────────────────────────────────────────────────────────
// Theme (Dark Ninja default, Light, Auto = follow the device) and candle style, saved to the account
// via appearance.js (/api/users/appearance). Unset candle values fall back to the theme's own.
const THEMES = [
  { id: 'dark-ninja', name: 'Dark Ninja', note: 'Default', swatch: ['#0B0E13', '#2BC48A', '#F2617A'] },
  { id: 'light',      name: 'Light',      note: 'Paper and indigo', swatch: ['#F6F3EC', '#1E7F52', '#C9402C'] },
  { id: 'auto',       name: 'Auto',       note: 'Follows your device', swatch: null },
];
const UP_SWATCHES   = ['#2BC48A', '#1E7F52', '#22B8CF', '#4C8DFF', '#E6EAF0'];
const DOWN_SWATCHES = ['#F2617A', '#C9402C', '#F28C3A', '#D45BD8', '#5B6575'];
const BODIES = [['solid', 'Solid'], ['hollow', 'Hollow'], ['hollow_up', 'Hollow up']];
// Fixed sample candles [open, high, low, close] for the preview.
const SAMPLE = [[50,53,48,52],[52,55,51,54],[54,55,49,50],[50,52,47,48],[48,51,46,50],[50,56,49,55],[55,58,53,57],[57,58,52,53],[53,54,50,51],[51,57,50,56],[56,60,55,59],[59,61,55,56],[56,57,52,54],[54,59,53,58],[58,63,57,62],[62,64,58,59]];

function AppearanceSection() {
  const [a, setA] = useState(getAppearance());
  const [msg, setMsg] = useState('');
  const c = a.candles || {};
  const css = k => getComputedStyle(document.documentElement).getPropertyValue(k).trim();
  const save = async next => {
    setA(next); setMsg('');
    try { await saveAppearance(next); setMsg('Saved'); } catch (_) { setMsg('Could not save'); }
  };
  const setCandle = (k, v) => save({ ...a, candles: { ...c, [k]: v } });
  // Effective values: the user's choice, else the current theme's default.
  const up = c.up || css('--candle-up'), down = c.down || css('--candle-down');
  const body = c.body || css('--candle-body') || 'solid', wick = c.wick || css('--candle-wick') || 'match';
  const seg = on => ({ flex: 1, minHeight: '36px', border: 'none', cursor: 'pointer', fontSize: '13px', fontFamily: "'Space Grotesk',sans-serif",
    background: on ? 'var(--accent)' : 'none', color: on ? 'var(--on-accent)' : 'var(--text-muted)', fontWeight: on ? 600 : 400 });
  const chip = (col, on) => ({ width: '32px', height: '32px', borderRadius: '8px', background: col, cursor: 'pointer',
    border: on ? '3px solid var(--text)' : '1px solid var(--border)' });
  const W = 16 * 22, H = 120, y = p => H - 6 - (p - 46) / 18 * (H - 12);
  return (
    <div style={S.card}>
      <div style={{ ...S.label, marginBottom: '8px' }}>Theme</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '20px' }}>
        {THEMES.map(t => (
          <label key={t.id} style={{ border: a.theme === t.id ? '2px solid var(--accent)' : '1px solid var(--border)', borderRadius: '10px', padding: '10px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ height: '44px', borderRadius: '6px', overflow: 'hidden', display: 'flex', border: '1px solid var(--border)' }}>
              {t.swatch
                ? <div style={{ flex: 1, background: t.swatch[0], display: 'flex', alignItems: 'flex-end', gap: '4px', padding: '6px' }}>
                    <span style={{ width: '7px', height: '20px', background: t.swatch[1], borderRadius: '2px' }} />
                    <span style={{ width: '7px', height: '13px', background: t.swatch[2], borderRadius: '2px' }} />
                    <span style={{ width: '7px', height: '28px', background: t.swatch[1], borderRadius: '2px' }} />
                  </div>
                : <><div style={{ flex: 1, background: '#0B0E13' }} /><div style={{ flex: 1, background: '#F6F3EC' }} /></>}
            </div>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600 }}>
              <input type="radio" name="theme" checked={a.theme === t.id} onChange={() => save({ ...a, theme: t.id })} /> {t.name}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t.note}</span>
          </label>
        ))}
      </div>

      <div style={{ ...S.label, marginBottom: '8px' }}>Candles</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
        <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[['up', 'Up colour', UP_SWATCHES, up], ['down', 'Down colour', DOWN_SWATCHES, down]].map(([k, label, sw, cur]) => (
            <div key={k}>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{label}</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {sw.map(col => <button key={col} type="button" aria-label={col} style={chip(col, cur.toUpperCase() === col)} onClick={() => setCandle(k, col)} />)}
                <input type="color" aria-label={`Custom ${label.toLowerCase()}`} value={cur.length === 7 ? cur : '#000000'} onChange={e => setCandle(k, e.target.value.toUpperCase())} style={{ width: '40px', height: '32px', border: '1px solid var(--border)', borderRadius: '8px', padding: 0, cursor: 'pointer' }} />
              </div>
            </div>
          ))}
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Body</div>
            <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
              {BODIES.map(([id, label]) => <button key={id} type="button" style={seg(body === id)} onClick={() => setCandle('body', id)}>{label}</button>)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Wicks and outline</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{ display: 'flex', flex: 1, border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
                <button type="button" style={seg(wick === 'match')} onClick={() => setCandle('wick', 'match')}>Match candle</button>
                <button type="button" style={seg(wick !== 'match')} onClick={() => setCandle('wick', wick !== 'match' ? wick : '#000000')}>One colour</button>
              </div>
              {wick !== 'match' && <input type="color" aria-label="Wick colour" value={wick.length === 7 ? wick : '#000000'} onChange={e => setCandle('wick', e.target.value.toUpperCase())} style={{ width: '40px', height: '36px', border: '1px solid var(--border)', borderRadius: '8px', padding: 0, cursor: 'pointer' }} />}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button type="button" style={S.btnGhost} onClick={() => save({ ...a, candles: {} })}>Reset candles to theme default</button>
            {msg && <span style={{ fontSize: '12px', color: msg === 'Saved' ? 'var(--success)' : 'var(--error)' }}>{msg}</span>}
          </div>
        </div>
        <div style={{ flex: '1 1 300px' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px' }}>Preview</div>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Candle preview" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px' }}>
            {SAMPLE.map(([o, h, l, cl], i) => {
              const isUp = cl >= o, col = isUp ? up : down, line = wick === 'match' ? col : wick;
              const hollow = body === 'hollow' || (body === 'hollow_up' && isUp), x = i * 22 + 11;
              return (
                <g key={i}>
                  <line x1={x} x2={x} y1={y(h)} y2={y(l)} stroke={line} strokeWidth="1.5" />
                  <rect x={x - 7} y={y(Math.max(o, cl))} width="14" height={Math.max(1.5, Math.abs(y(o) - y(cl)))} fill={hollow ? 'var(--surface)' : col} stroke={line} strokeWidth="1.5" />
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    </div>
  );
}

function ChartIndicatorsSection() {
  const [prefs, setPrefs] = useState(null);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    api.get('/users/chart-prefs').then(r => setPrefs({ favourites: [], ...(r.data.prefs || {}) })).catch(() => setPrefs({ favourites: [] }));
  }, []);
  if (!prefs) return <div style={S.empty}>Loading…</div>;
  const fav = new Set(prefs.favourites || []);
  const toggle = async id => {
    const next = { ...prefs, favourites: fav.has(id) ? [...fav].filter(x => x !== id) : [...fav, id] };
    setPrefs(next);
    try { await api.put('/users/chart-prefs', { prefs: next }); setMsg('Saved'); } catch (_) { setMsg('Could not save'); }
    setTimeout(() => setMsg(''), 1500);
  };
  const group = pane => Object.entries(INDICATORS).filter(([, d]) => d.pane === pane);
  return (
    <div style={S.card}>
      <div style={{ ...S.cardMeta, marginBottom: '12px' }}>Tick the indicators you use most. They appear first in the chart's indicator list. {msg && <b style={{ color: 'var(--accent)' }}>{msg}</b>}</div>
      {[['On price', 'price'], ['Own pane', 'own']].map(([title, pane]) => (
        <div key={pane} style={{ marginBottom: '12px' }}>
          <span style={S.label}>{title}</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '4px 12px' }}>
            {group(pane).map(([id, d]) => (
              <label key={id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '3px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={fav.has(id)} onChange={() => toggle(id)} style={{ accentColor: 'var(--accent)' }} />
                {d.name}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Annotations section ───────────────────────────────────────────────────────
// Search box + optional filter for the long Lists / Annotations tabs.
function Toolbar({ query, setQuery, placeholder, children, count }) {
  return (
    <div style={S.toolbar}>
      <input style={{ ...S.input, flex: '1 1 200px', width: 'auto' }} value={query} onChange={e => setQuery(e.target.value)} placeholder={placeholder} aria-label={placeholder} />
      {children}
      <span style={S.count}>{count}</span>
    </div>
  );
}

// "Show more" paging so hundreds of rows don't render at once.
const PAGE = 50;
function ShowMore({ shown, total, onMore }) {
  if (shown >= total) return null;
  return <button style={{ ...S.btnGhost, width: '100%', padding: '10px', marginTop: '10px', fontSize: '12px' }} onClick={onMore}>Show more ({total - shown} left)</button>;
}

// ── My annotations (search, privacy filter, sort, paging) ─────────────────────
function MyAnnotations({ mine, setMine }) {
  const [expanded, setExpanded] = useState({}); // ann.id -> bool for share manager
  const [query,    setQuery]    = useState('');
  const [privacy,  setPrivacy]  = useState('All');
  const [sort,     setSort]     = useState('recent');
  const [limit,    setLimit]    = useState(PAGE);

  const updatePrivacy = async (ann, level) => {
    try {
      await api.post('/annotations/privacy', { annotation_id: ann.id, privacy_level: level });
      setMine(prev => prev.map(a => a.id === ann.id ? { ...a, privacy_level: level } : a));
      // Collapse share manager if switching away from Share
      if (level !== 'Share') setExpanded(prev => ({ ...prev, [ann.id]: false }));
    } catch { /* ignore */ }
  };

  const deleteAnn = async (ann) => {
    if (!window.confirm(`Delete annotations for ${ann.ticker}? This cannot be undone.`)) return;
    try {
      await api.delete('/annotations/delete', { params: { id: ann.id } });
      setMine(prev => prev.filter(a => a.id !== ann.id));
    } catch { /* ignore */ }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const q = query.trim().toUpperCase();
  const rows = mine
    .filter(a => (!q || a.ticker.toUpperCase().includes(q)) && (privacy === 'All' || a.privacy_level === privacy))
    .sort((a, b) => sort === 'ticker' ? a.ticker.localeCompare(b.ticker) : String(b.last_edited || '').localeCompare(String(a.last_edited || '')));

  if (mine.length === 0) return <div style={S.empty}>You have no saved annotations. Drawings you save on a chart appear here.</div>;

  return (
    <>
      <Toolbar query={query} setQuery={v => { setQuery(v); setLimit(PAGE); }} placeholder="Search ticker…" count={`${rows.length} of ${mine.length}`}>
        <select style={S.select} value={privacy} onChange={e => { setPrivacy(e.target.value); setLimit(PAGE); }} aria-label="Privacy filter">
          {['All', 'Private', 'Public', 'Share'].map(v => <option key={v} value={v}>{v === 'All' ? 'All privacy' : v}</option>)}
        </select>
        <select style={S.select} value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort">
          <option value="recent">Last edited</option>
          <option value="ticker">Ticker A-Z</option>
        </select>
      </Toolbar>
      {rows.length === 0 ? <div style={S.empty}>No annotations match.</div> : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Ticker</th>
                <th style={S.th}>Type</th>
                <th style={S.th}>Privacy</th>
                <th style={S.th}>Last edited</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, limit).map(ann => (
                <React.Fragment key={ann.id}>
                  <tr
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={S.td}>
                      <button style={S.tickerLink} onClick={() => openChart(ann.ticker)}>{ann.ticker}</button>
                    </td>
                    <td style={S.td}>{ann.chart_type}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                      <select style={S.select} value={ann.privacy_level} onChange={e => updatePrivacy(ann, e.target.value)}>
                        <option value="Private">Private</option>
                        <option value="Public">Public</option>
                        <option value="Share">Share</option>
                      </select>
                      {ann.privacy_level === 'Share' && (
                        <button style={{ ...S.expandBtn, marginLeft: '6px' }} onClick={() => toggleExpand(ann.id)}>
                          {expanded[ann.id] ? '▲' : '▼ Manage'}
                        </button>
                      )}
                    </td>
                    <td style={S.td}>{ann.last_edited ? new Date(ann.last_edited).toLocaleDateString() : '—'}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      <button style={S.btnDanger} onClick={() => deleteAnn(ann)}>Delete</button>
                    </td>
                  </tr>
                  {ann.privacy_level === 'Share' && expanded[ann.id] && (
                    <tr>
                      <td colSpan={5} style={{ ...S.td, background: 'var(--bg)' }}>
                        <ShareManager ann={ann} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ShowMore shown={limit} total={rows.length} onMore={() => setLimit(l => l + PAGE)} />
    </>
  );
}

// ── Annotations shared with me (search, paging) ───────────────────────────────
function SharedAnnotations({ sharedWith }) {
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const q = query.trim().toUpperCase();
  const rows = sharedWith.filter(a => !q || a.ticker.toUpperCase().includes(q) || String(a.shared_by || '').toUpperCase().includes(q));

  if (sharedWith.length === 0) return <div style={S.empty}>No annotations have been shared with you.</div>;

  return (
    <>
      <Toolbar query={query} setQuery={v => { setQuery(v); setLimit(PAGE); }} placeholder="Search ticker or user…" count={`${rows.length} of ${sharedWith.length}`} />
      {rows.length === 0 ? <div style={S.empty}>Nothing matches.</div> : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Ticker</th>
                <th style={S.th}>Type</th>
                <th style={S.th}>Shared by</th>
                <th style={S.th}>Shared</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, limit).map(ann => (
                <tr key={ann.id}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={S.td}>
                    <button style={S.tickerLink} onClick={() => openChart(ann.ticker)}>{ann.ticker}</button>
                  </td>
                  <td style={S.td}>{ann.chart_type}</td>
                  <td style={S.td}>{ann.shared_by}</td>
                  <td style={S.td}>{ann.shared_at ? new Date(ann.shared_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ShowMore shown={limit} total={rows.length} onMore={() => setLimit(l => l + PAGE)} />
    </>
  );
}

// ── My lists (search, paging; the create form opens on demand) ────────────────
function ListsSection({ lists, loaded, onCreated, onDeleted, onUpdated }) {
  const [query,    setQuery]    = useState('');
  const [limit,    setLimit]    = useState(PAGE);
  const [creating, setCreating] = useState(false);
  const q = query.trim().toLowerCase();
  const rows = lists.filter(l => !q || l.name.toLowerCase().includes(q) || String(l.description || '').toLowerCase().includes(q));

  return (
    <>
      <div style={S.toolbar}>
        <input style={{ ...S.input, flex: '1 1 200px', width: 'auto' }} value={query} onChange={e => { setQuery(e.target.value); setLimit(PAGE); }} placeholder="Search lists…" aria-label="Search lists" />
        <span style={S.count}>{rows.length} of {lists.length}</span>
        <button style={{ ...S.btn, padding: '8px 14px' }} onClick={() => setCreating(v => !v)}>{creating ? 'Cancel' : '+ New list'}</button>
      </div>
      {creating && <CreateListForm onCreated={l => { onCreated(l); setCreating(false); }} />}
      {loaded && lists.length === 0 && <div style={S.empty}>You have no lists yet. Use "+ New list" to create one.</div>}
      {lists.length > 0 && rows.length === 0 && <div style={S.empty}>No lists match.</div>}
      {rows.slice(0, limit).map(list => (
        <ListCard key={list.id} list={list} onDeleted={onDeleted} onUpdated={onUpdated} />
      ))}
      <ShowMore shown={limit} total={rows.length} onMore={() => setLimit(l => l + PAGE)} />
    </>
  );
}

// ── Settings page ─────────────────────────────────────────────────────────────
// Tabs: a sidebar on wide screens, a scrolling tab bar on phones. The open tab is in the URL (?tab=).
const TABS = [
  { id: 'appearance', label: 'Appearance',       desc: 'Theme and candle style. Changes save to your account straight away.' },
  { id: 'indicators', label: 'Chart indicators', desc: 'The indicators you use, shown first on the chart.' },
  { id: 'lists',      label: 'Lists',            desc: 'Your watchlists. Open a list to see, rank or edit its tickers.' },
  { id: 'annotations', label: 'Annotations',     desc: 'Saved chart drawings and who can see them.' },
  { id: 'shared',     label: 'Shared with me',   desc: 'Drawings other users have shared with you.' },
  { id: 'account',    label: 'Account',          desc: 'Your account details.' },
];

function useWide(px = 760) {
  const q = `(min-width: ${px}px)`;
  const [wide, setWide] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q), on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [q]);
  return wide;
}

export default function Settings() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const wide = useWide();
  const tab = TABS.find(t => t.id === params.get('tab')) || TABS[0];

  const [lists,       setLists]       = useState([]);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [mine,        setMine]        = useState(null);
  const [sharedWith,  setSharedWith]  = useState(null);

  useEffect(() => { document.title = 'Settings | chart.ninja'; }, []);

  useEffect(() => {
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get('/lists')
      .then(r => setLists(r.data.lists || []))
      .catch(() => {})
      .finally(() => setListsLoaded(true));
    Promise.all([api.get('/annotations/list'), api.get('/annotations/shared-with-me')])
      .then(([a, b]) => { setMine(a.data.annotations || []); setSharedWith(b.data.annotations || []); })
      .catch(() => { setMine([]); setSharedWith([]); });
  }, [user]);

  const handleListCreated = (list)    => setLists(prev => [...prev, list]);
  const handleListDeleted = (id)      => setLists(prev => prev.filter(l => l.id !== id));
  const handleListUpdated = (updated) => setLists(prev => prev.map(l => l.id === updated.id ? { ...l, ...updated } : l));

  if (loading || !user) return <div style={S.loadingScreen}>Loading…</div>;

  const counts = { lists: listsLoaded ? lists.length : null, annotations: mine ? mine.length : null, shared: sharedWith ? sharedWith.length : null };
  const pick = id => setParams(id === TABS[0].id ? {} : { tab: id }, { replace: true });
  const loadingRow = <div style={S.empty}>Loading…</div>;

  return (
    <div style={S.page}>
      <Navbar />

      <div style={{ ...S.content, padding: wide ? '40px 32px' : '20px 16px' }}>
        <h1 style={S.pageTitle}>Settings</h1>
        <p style={{ ...S.pageSub, marginBottom: wide ? '28px' : '16px' }}>Signed in as {user.username}</p>

        <div style={{ display: 'flex', flexDirection: wide ? 'row' : 'column', gap: wide ? '32px' : '16px', alignItems: 'flex-start' }}>
          <nav aria-label="Settings sections" style={wide ? S.sideNav : S.tabBar}>
            {TABS.map(t => {
              const on = t.id === tab.id, n = counts[t.id];
              return (
                <button key={t.id} type="button" onClick={() => pick(t.id)} aria-current={on ? 'page' : undefined}
                  style={wide ? S.sideItem(on) : S.tabItem(on)}>
                  <span>{t.label}</span>
                  {n != null && <span style={S.tabCount(on)}>{n}</span>}
                </button>
              );
            })}
          </nav>

          <section style={{ flex: 1, minWidth: 0, width: '100%' }}>
            <div style={S.panelHead}>
              <h2 style={S.panelTitle}>{tab.label}</h2>
              <p style={S.panelDesc}>{tab.desc}</p>
            </div>
            {tab.id === 'appearance'  && <AppearanceSection />}
            {tab.id === 'indicators'  && <ChartIndicatorsSection />}
            {tab.id === 'lists'       && <ListsSection lists={lists} loaded={listsLoaded} onCreated={handleListCreated} onDeleted={handleListDeleted} onUpdated={handleListUpdated} />}
            {tab.id === 'annotations' && (mine ? <MyAnnotations mine={mine} setMine={setMine} /> : loadingRow)}
            {tab.id === 'shared'      && (sharedWith ? <SharedAnnotations sharedWith={sharedWith} /> : loadingRow)}
            {tab.id === 'account'     && (
              <div style={S.card}>
                <div style={S.kv}><span style={S.kvKey}>Username</span><span>{user.username}</span></div>
                <div style={S.kv}><span style={S.kvKey}>Role</span><span>{user.is_admin ? 'Admin' : 'Member'}</span></div>
                <div style={{ ...S.cardMeta, marginTop: '12px' }}>More account settings are coming soon.</div>
              </div>
            )}
            {/* Discord Alerts (DiscordSection) is parked: alerts go to the #custom-alerts channel for now. */}
          </section>
        </div>
      </div>
    </div>
  );
}
